/**
 * FileDropZone — the File destination's empty state.
 *
 * A dashed target that accepts both a click (opens the OS picker) and a drag.
 * The drag half is implemented rather than decorative: a dashed drop-zone that
 * silently ignores a dropped file is a worse lie than having no dashed border
 * at all. Everything here is plain DOM — no platform API — so it stays inside
 * the client-ui layering rules.
 *
 * The host supplies the size limit as pre-formatted text. It is NOT hardcoded:
 * the effective ceiling comes from `capabilities.maxBytes`, which in turn
 * derives from the protocol's part-count bound, and the server can tighten it
 * further via its own quota. A number typed into 28 locale files would go
 * stale the moment either changes.
 */
import { UploadCloud01 } from "@untitledui/icons";
import { type DragEvent, useRef, useState } from "react";
import styles from "./FileDropZone.module.css";

export interface FileDropZoneLabels {
  /** Primary line, e.g. "Drag & drop here". */
  title: string;
  /**
   * Secondary line stating the real limit, e.g. "Files up to 4 GB." The Figma
   * says "Transfer any file. No size limit.", which is false — see the spec.
   */
  limit: string;
  /**
   * Accessible name for the zone, which is a button.
   *
   * MUST contain `title` verbatim: an aria-label overrides the visible text,
   * and WCAG 2.5.3 (Label in Name) requires the visible label to be part of
   * the accessible name, so that voice control ("click drag and drop here")
   * works. The shipped strings start with the visible line for that reason.
   */
  ariaLabel: string;
}

export interface FileDropZoneProps {
  onFileSelected: (file: File) => void;
  labels: FileDropZoneLabels;
  disabled?: boolean;
}

export function FileDropZone({ onFileSelected, labels, disabled = false }: FileDropZoneProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);

  const take = (files: FileList | null) => {
    const file = files?.[0];
    if (file) onFileSelected(file);
  };

  const onDrop = (e: DragEvent<HTMLButtonElement>) => {
    e.preventDefault();
    setDragging(false);
    if (!disabled) take(e.dataTransfer.files);
  };

  return (
    <>
      <button
        type="button"
        className={dragging ? `${styles.zone} ${styles.dragging}` : styles.zone}
        aria-label={labels.ariaLabel}
        disabled={disabled}
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => {
          // preventDefault regardless: without it the browser navigates away to
          // the dropped file, which is worse than a swallowed drop.
          e.preventDefault();
          if (disabled) {
            // Otherwise the cursor promises a drop that the handler will refuse.
            e.dataTransfer.dropEffect = "none";
            return;
          }
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
      >
        {/* Children are pointer-events: none so dragging over them does not
            fire dragleave on the button — see the CSS. */}
        <span className={styles.glyph} aria-hidden="true">
          <UploadCloud01 className={styles.icon} />
        </span>
        <span className={styles.text}>
          <span className={styles.title}>{labels.title}</span>
          <span className={styles.limit}>{labels.limit}</span>
        </span>
      </button>
      {/* Kept out of the accessible tree: the button above is the control, and
          exposing both would announce two file pickers for one affordance. */}
      <input
        ref={inputRef}
        type="file"
        className={styles.input}
        tabIndex={-1}
        aria-hidden="true"
        onChange={(e) => {
          take(e.target.files);
          // Reset so re-picking the SAME file fires change again.
          e.target.value = "";
        }}
      />
    </>
  );
}
